import React, { useState, useEffect } from "react"
import { Modal, Button, Select, Spin } from "antd"
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons"
import { cloneDeep } from "lodash"
import { gen, swimminglyApi } from "./utils"
import SingleRank from "./SingleRank"

const { Option } = Select

const defaultIndividualScoring = [
  { place: 1, points: 5 },
  { place: 2, points: 3 },
  { place: 3, points: 1 },
]

const defaultRelayScoring = [
  { place: 1, points: 10 },
  { place: 2, points: 5 },
]

const UpdateVirtualMeetScoringModal = (props) => {
  const [individualScoring, setIndividualScoring] = useState(
    defaultIndividualScoring,
  )
  const [relayScoring, setRelayScoring] = useState(defaultRelayScoring)
  const [individualScoringLimit, setIndividualScoringLimit] = useState(null)
  const [relayScoringLimit, setRelayScoringLimit] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!props.visible || !props.meet?.virtualMeetId) {
      return undefined
    }

    let mounted = true

    const loadScoring = async () => {
      const [scoringConfigResponse, scoringLimitResponse] = await Promise.all([
        swimminglyApi
          .post(gen("/api/getScoringConfig"))
          .body({ virtualMeetId: props.meet.virtualMeetId }),
        swimminglyApi
          .post(gen("/api/getScoringLimit"))
          .body({ virtualMeetId: props.meet.virtualMeetId }),
      ])

      if (!mounted) {
        return
      }

      if (scoringConfigResponse.status === "error") {
        console.error("problem loading scoring data")
      } else if (scoringConfigResponse.scoringConfig) {
        let individualScoringData = scoringConfigResponse.scoringConfig
          .filter((row) => row.isRelay === 0)
          .map((obj) => {
            return {
              place: obj.place,
              points: obj.points,
            }
          })
        let relayScoringData = scoringConfigResponse.scoringConfig
          .filter((row) => row.isRelay === 1)
          .map((obj) => {
            return {
              place: obj.place,
              points: obj.points,
            }
          })
        setIndividualScoring(individualScoringData)
        setRelayScoring(relayScoringData)
      }

      if (scoringLimitResponse.status === "error") {
        console.error("problem loading scoring data")
      } else {
        const scoringLimits = scoringLimitResponse.scoringLimit || {}

        if (!scoringLimits.individualScoringLimit) {
          setIndividualScoringLimit(null)
        } else {
          setIndividualScoringLimit(
            parseInt(scoringLimits.individualScoringLimit),
          )
        }

        if (!scoringLimits.relayScoringLimit) {
          setRelayScoringLimit(null)
        } else {
          setRelayScoringLimit(parseInt(scoringLimits.relayScoringLimit))
        }
      }
    }

    loadScoring()

    return () => {
      mounted = false
    }
  }, [props.visible, props.meet?.virtualMeetId])

  const onCancel = () => {
    unMountThis()
    props.unMountParent()
  }

  const onOk = async () => {
    setLoading(true)
    let getUpdateScoringResult = fetch(gen("/api/updateScoringConfig"), {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify({
        virtualMeetId: props.meet.virtualMeetId,
        relayScoring,
        individualScoring,
      }),
    }).then((response) => response.json())
    let getUpdateScoringLimitResult = fetch(gen("/api/updateScoringLimit"), {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify({
        virtualMeetId: props.meet.virtualMeetId,
        relayScoringLimit,
        individualScoringLimit,
      }),
    }).then((response) => response.json())

    let [updateScoringResult, updateScoringLimitResult] = await Promise.all([
      getUpdateScoringResult,
      getUpdateScoringLimitResult,
    ])

    if (
      updateScoringResult.status === "error" ||
      updateScoringLimitResult.status === "error"
    ) {
      console.error("something went wrong")
    }
    unMountThis()
    props.unMountParent()
    props.refreshPage()
  }

  const unMountThis = () => {
    setIndividualScoring(defaultIndividualScoring)
    setRelayScoring(defaultRelayScoring)
    setIndividualScoringLimit(null)
    setRelayScoringLimit(null)
    setLoading(false)
  }

  const changePlace = (type, index, place, points) => {
    if (type === "individual") {
      let newIndividualScoring = cloneDeep(individualScoring)
      let obj = individualScoring[index]
      if (place) {
        obj.place = place
      }
      if (points) {
        obj.points = points
      }
      newIndividualScoring[index] = obj
      setIndividualScoring(newIndividualScoring)
    }
    if (type === "relay") {
      let newRelayScoring = cloneDeep(relayScoring)
      let obj = relayScoring[index]
      if (place) {
        obj.place = place
      }
      if (points) {
        obj.points = points
      }
      newRelayScoring[index] = obj
      setRelayScoring(newRelayScoring)
    }
  }

  const removeIndex = (type, index) => {
    if (type === "individual") {
      let newIndividualScoring = cloneDeep(individualScoring)
      newIndividualScoring.splice(index, 1)
      setIndividualScoring(newIndividualScoring)
    }
    if (type === "relay") {
      let newRelayScoring = cloneDeep(relayScoring)
      newRelayScoring.splice(index, 1)
      setRelayScoring(newRelayScoring)
    }
  }

  const add = (type) => {
    if (type === "individual") {
      let newIndividualScoring = cloneDeep(individualScoring)
      let maxPlace = Math.max(...newIndividualScoring.map((el) => el.place), 0)
      newIndividualScoring.push({ place: maxPlace + 1, points: 1 })
      setIndividualScoring(newIndividualScoring)
    } else if (type === "relay") {
      let newRelayScoring = cloneDeep(relayScoring)
      let maxPlace = Math.max(...newRelayScoring.map((el) => el.place), 0)
      newRelayScoring.push({ place: maxPlace + 1, points: 1 })
      setRelayScoring(newRelayScoring)
    }
  }

  return (
    <Modal
      title="Update Scoring"
      loading={loading}
      open={props.visible}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          style={{ minWidth: "70px" }}
          key="submit"
          type="primary"
          loading={loading}
          onClick={onOk}
        >
          {loading ? (
            <Spin
              indicator={
                <div
                  style={{
                    color: "white",
                  }}
                >
                  <LoadingOutlined style={{ fontSize: 24 }} spin />
                </div>
              }
            />
          ) : (
            "Ok"
          )}
        </Button>,
      ]}
    >
      <div>
        <h4>Update Scoring:</h4>
        <div style={{ display: "flex", justifyContent: "space-around" }}>
          <div>
            <div style={{ textAlign: "center", fontSize: "20px" }}>
              Individual
            </div>
            <div style={{ display: "flex" }}>
              <div style={{ width: "20%" }} />
              <div style={{ width: "100px" }}>Place</div>
              <div style={{ width: "100px" }}>Points</div>
            </div>
            {individualScoring.map((el, i) => (
              <SingleRank
                key={`individual_${el.place}_${i}`}
                place={el.place}
                points={el.points}
                changePlace={(place) =>
                  changePlace("individual", i, place, null)
                }
                changePoints={(points) =>
                  changePlace("individual", i, null, points)
                }
                remove={() => removeIndex("individual", i)}
              />
            ))}
            <Button
              onClick={() => add("individual")}
              shape="circle"
              icon={<PlusOutlined />}
              style={{ marginTop: "5px" }}
            />
            <hr />
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ marginRight: "10px" }}>
                Individual Scoring Limit:
              </div>

              <Select
                style={{ minWidth: "70px" }}
                value={individualScoringLimit}
                onChange={(val) => setIndividualScoringLimit(val)}
              >
                <Option value={null}>None</Option>
                <Option value={1}>1</Option>
                <Option value={2}>2</Option>
                <Option value={3}>3</Option>
                <Option value={4}>4</Option>
                <Option value={5}>5</Option>
              </Select>
            </div>
          </div>
          <div>
            <div style={{ textAlign: "center", fontSize: "20px" }}>Relay</div>
            <div style={{ display: "flex" }}>
              <div style={{ width: "20%" }} />
              <div style={{ width: "100px" }}>Place</div>
              <div style={{ width: "100px" }}>Points</div>
            </div>
            {relayScoring.map((el, i) => (
              <SingleRank
                key={`relay_${el.place}_${i}`}
                place={el.place}
                points={el.points}
                changePlace={(place) => changePlace("relay", i, place, null)}
                changePoints={(points) => changePlace("relay", i, null, points)}
                remove={() => removeIndex("relay", i)}
              />
            ))}
            <Button
              onClick={() => add("relay")}
              shape="circle"
              icon={<PlusOutlined />}
              style={{ marginTop: "5px" }}
            />
            <hr />
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ marginRight: "10px" }}>Relay Scoring Limit:</div>

              <Select
                style={{ minWidth: "70px" }}
                value={relayScoringLimit}
                onChange={(val) => setRelayScoringLimit(val)}
              >
                <Option value={null}>None</Option>
                <Option value={1}>1</Option>
                <Option value={2}>2</Option>
                <Option value={3}>3</Option>
                <Option value={4}>4</Option>
                <Option value={5}>5</Option>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default UpdateVirtualMeetScoringModal
